import cryptoStore from './CryptoDataStore';

const SOL_PRICE_REFRESH_MS = 30000;
const BATCH_PRICE_REFRESH_MS = 5000;
const WORKER_URL = 'https://ape-data-proxy.hello-maturevats.workers.dev';

const SOLANA_WS_ENDPOINTS = [
  'wss://api.mainnet-beta.solana.com',
  'wss://solana-rpc.publicnode.com',
];

class WebSocketManager {
  private yodaoWs: WebSocket | undefined;
  private yodaoId = `tg-${Math.floor(Math.random() * 1000000)}`;
  private solRpcWs: WebSocket | undefined;
  private binanceSolWs: WebSocket | undefined;
  private binanceEthWs: WebSocket | undefined;
  private binanceBnbWs: WebSocket | undefined;
  private subscribedMints = new Set<string>();
  private solRpcEndpointIndex = 0;
  private solPriceUsd = 0;
  private ethPriceUsd = 0;
  private bnbPriceUsd = 0;

  constructor() {
    this.connectBinanceTickers();
    this.connectYodao();
    this.connectSolanaRpc();
    this.fetchSolPrice();
    this.startBatchPolling();
  }

  // Real-time Reference Spot Prices from Binance WebSocket
  private connectBinanceTickers() {
    this.connectBinanceStream('solusdt', (price) => {
      this.solPriceUsd = price;
    });
    this.connectBinanceStream('ethusdt', (price) => {
      this.ethPriceUsd = price;
    });
    this.connectBinanceStream('bnbusdt', (price) => {
      this.bnbPriceUsd = price;
    });
  }

  private connectBinanceStream(pair: string, onPrice: (price: number) => void) {
    try {
      const ws = new WebSocket(`wss://stream.binance.com:9443/ws/${pair}@ticker`);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.c) {
            const price = parseFloat(data.c);
            if (price > 0) onPrice(price);
          }
        } catch {
          // ignore
        }
      };
      ws.onerror = () => {};
      ws.onclose = () => {
        setTimeout(() => this.connectBinanceStream(pair, onPrice), 5000);
      };

      if (pair === 'solusdt') this.binanceSolWs = ws;
      if (pair === 'ethusdt') this.binanceEthWs = ws;
      if (pair === 'bnbusdt') this.binanceBnbWs = ws;
    } catch {
      // Fallback handles it
    }
  }

  // Direct Solana RPC WebSocket logsSubscribe for zero-rate-limit mint trade monitoring
  private connectSolanaRpc() {
    const endpoint = SOLANA_WS_ENDPOINTS[this.solRpcEndpointIndex];
    try {
      this.solRpcWs = new WebSocket(endpoint);

      this.solRpcWs.onopen = () => {
        this.subscribedMints.forEach((mint) => {
          this.sendSolanaSubscription(mint);
        });
      };

      this.solRpcWs.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.method === 'logsNotification' && data.params?.result?.value) {
            const logVal = data.params.result.value;
            const logs: string[] = logVal.logs || [];
            // Parse Pump.fun and Raydium trade logs
            const isPump = logs.some((l) => l.includes('6EF8rrecthR5Dkzon8Nwu78hRvfCKubJ14M5uBEwF6P'));
            const isRaydium = logs.some((l) => l.includes('675kPX9MHTjS2zt1qfr1NYHuzeLXfQM9H24wFSUt1Mp8'));
            if (isPump || isRaydium) {
              // Trigger fresh update for active tokens
              // eslint-disable-next-line no-console
              console.log(`[SOLANA WS] Real-time trade detected (${isPump ? 'Pump.fun' : 'Raydium'})`);
            }
          }
        } catch {
          // ignore parse errors
        }
      };

      this.solRpcWs.onerror = () => {};

      this.solRpcWs.onclose = () => {
        this.solRpcEndpointIndex = (this.solRpcEndpointIndex + 1) % SOLANA_WS_ENDPOINTS.length;
        setTimeout(() => this.connectSolanaRpc(), 4000);
      };
    } catch {
      // fallback
    }
  }

  private sendSolanaSubscription(mint: string) {
    if (this.solRpcWs?.readyState === WebSocket.OPEN) {
      this.solRpcWs.send(JSON.stringify({
        jsonrpc: '2.0',
        id: Date.now(),
        method: 'logsSubscribe',
        params: [
          { mentions: [mint] },
          { commitment: 'confirmed' },
        ],
      }));
    }
  }

  // Poll prices for all active tokens every 5s across all chains
  private startBatchPolling() {
    setInterval(async () => {
      try {
        const activeTokens = cryptoStore.getActiveTokens();
        if (activeTokens.length === 0) return;

        const res = await fetch(`${WORKER_URL}/api/batch-prices`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tokens: activeTokens.map((t) => ({ chain: t.chain, address: t.address })) }),
        });

        if (!res.ok) return;
        const data = await res.json();

        if (data.pairs && Array.isArray(data.pairs)) {
          data.pairs.forEach((pair: any) => {
            const token = activeTokens.find((t) => t.address === pair.baseToken?.address);
            if (token) {
              cryptoStore.updateToken(token.key, {
                priceUsd: parseFloat(pair.priceUsd || '0'),
                priceChange24h: pair.priceChange?.h24,
                marketCap: pair.marketCap || pair.fdv,
                volume24h: pair.volume?.h24,
                liquidity: pair.liquidity?.usd,
                txns24h: pair.txns?.h24,
              });
            }
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Batch price polling failed', e);
      }
    }, BATCH_PRICE_REFRESH_MS);
  }

  // Fallback SOL price from CoinGecko
  private async fetchSolPrice() {
    if (this.solPriceUsd > 0) {
      setTimeout(() => this.fetchSolPrice(), SOL_PRICE_REFRESH_MS);
      return;
    }
    try {
      const res = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd',
      );
      const data = await res.json();
      if (data.solana?.usd) {
        this.solPriceUsd = data.solana.usd;
      }
    } catch {
      if (this.solPriceUsd === 0) {
        this.solPriceUsd = 150; // Fallback only on first load
      }
    }
    setTimeout(() => this.fetchSolPrice(), SOL_PRICE_REFRESH_MS);
  }

  private connectYodao() {
    try {
      const apiKey = import.meta.env.VITE_YODAO_API_KEY || '83a1840567d856176cb1c09f0c46309705337c2d626d119eaba0cfb039aaea62';
      this.yodaoWs = new WebSocket(`wss://dev.yodao.io/?id=${this.yodaoId}&api_key=${apiKey}`);

      this.yodaoWs.onopen = () => {
        this.subscribedMints.forEach((mint) => {
          this.yodaoWs?.send(JSON.stringify({
            event: 'update_mint_info',
            data: { action: 'subscribe', id: this.yodaoId, mint },
          }));
        });
      };

      this.yodaoWs.onmessage = (event) => {
        try {
          const incoming = JSON.parse(event.data);

          if (incoming.type === 'ping') {
            this.yodaoWs?.send(JSON.stringify({ type: 'pong' }));
            return;
          }

          if (incoming.channel === 'update_mint_info' && incoming.data) {
            const tokenInfo = incoming.data;
            if (tokenInfo.mint && tokenInfo.m_cap_usd) {
              cryptoStore.updateToken(tokenInfo.mint, {
                marketCap: tokenInfo.m_cap_usd,
                priceUsd: tokenInfo.base_mint_price_usd,
              });
            }
          }
        } catch {
          // ignore
        }
      };

      this.yodaoWs.onerror = () => {};

      this.yodaoWs.onclose = () => {
        setTimeout(() => this.connectYodao(), 5000);
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Yodao WS failed', e);
    }
  }

  subscribeToPair(pair: any) {
    if (pair.chainId === 'solana' && pair.baseToken?.address) {
      const mint = pair.baseToken.address;
      if (!this.subscribedMints.has(mint)) {
        this.subscribedMints.add(mint);
        if (this.yodaoWs?.readyState === WebSocket.OPEN) {
          this.yodaoWs.send(JSON.stringify({
            event: 'update_mint_info',
            data: { action: 'subscribe', id: this.yodaoId, mint },
          }));
        }
        this.sendSolanaSubscription(mint);
      }
    }
  }

  subscribeByMint(mint: string, chain: string) {
    if (chain === 'solana') {
      this.subscribeToPair({ chainId: 'solana', baseToken: { address: mint } });
    }
  }

  public getSolPriceUsd() {
    return this.solPriceUsd || 180;
  }

  public getEthPriceUsd() {
    return this.ethPriceUsd || 2500;
  }

  public getBnbPriceUsd() {
    return this.bnbPriceUsd || 600;
  }
}

let wsManagerInstance: WebSocketManager | undefined;

export function getWebSocketManager() {
  if (!wsManagerInstance) {
    wsManagerInstance = new WebSocketManager();
  }
  return wsManagerInstance;
}
